import React from "react";
import { Phone, MessageCircle, ShieldCheck, Clock, Camera } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { telLink, waLink } from "@/lib/api";
import LiveResponseBadge from "@/components/conversion/LiveResponseBadge";
import NearestTechBadge from "@/components/conversion/NearestTechBadge";

const TRUST_ICONS = [ShieldCheck, Camera, Clock];

function HeroEyebrow({ label }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--brand-urgent))]/12 text-[hsl(var(--brand-urgent))] px-3 py-1 text-xs font-semibold tracking-wide"
      data-testid="hero-eyebrow"
    >
      <span className="inline-block h-2 w-2 rounded-full bg-[hsl(var(--brand-urgent))] animate-pulse" />
      {label}
    </span>
  );
}

function HeroTrust({ items }) {
  return (
    <div className="mt-5 flex flex-wrap gap-2" data-testid="hero-trust-row">
      {items.map((tx, i) => {
        const Icon = TRUST_ICONS[i] || ShieldCheck;
        return (
          <span
            key={`trust-${tx}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[hsl(var(--border))] px-3 py-1 text-xs font-medium text-[hsl(var(--foreground))]"
          >
            <Icon className="h-3.5 w-3.5 text-[hsl(var(--brand-aqua))]" /> {tx}
          </span>
        );
      })}
    </div>
  );
}

/**
 * Conversion-optimized CTA stack:
 *  - PRIMARY: full-width, urgent, "CHIAMA H24" (the action that converts most
 *    on mobile in an emergency). 56px tap target.
 *  - SECONDARY: smaller, neutral, WhatsApp (fallback for users who don't
 *    want to phone immediately).
 *
 * This shape mirrors the gold-standard pattern used by emergency services
 * (Roto-Rooter, BoilerJuice, etc.) where the phone CTA dominates the fold.
 */
function HeroCTAButtons({ callLabel }) {
  return (
    <div className="mt-6 flex flex-col gap-2.5 max-w-md">
      <a
        href={telLink()}
        className="inline-flex items-center justify-center gap-2 h-14 rounded-xl bg-[hsl(var(--brand-aqua))] hover:bg-[hsl(var(--brand-aqua-2))] text-white font-bold text-lg press shadow-md tracking-tight"
        data-testid="hero-call-button"
      >
        <Phone className="h-5 w-5" />
        <span>{callLabel}</span>
      </a>
      <a
        href={waLink()}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-white border border-[hsl(var(--border))] text-[hsl(var(--brand-navy))] font-semibold text-sm press"
        data-testid="hero-whatsapp-button"
      >
        <MessageCircle className="h-4 w-4 text-[hsl(var(--success))]" />
        <span>WhatsApp · scrivi se preferisci</span>
      </a>
    </div>
  );
}

function HeroContent({ t, lang }) {
  return (
    <div>
      <HeroEyebrow label={t("hero.eyebrow")} />
      <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[hsl(var(--brand-navy))] leading-[1.05]">
        <span className="block">{t("hero.h1_line1")}</span>
        <span className="block text-[hsl(var(--brand-aqua))]">{t("hero.h1_line2")}</span>
      </h1>
      <p className="mt-4 text-base sm:text-lg text-[hsl(var(--muted-foreground))] max-w-[55ch]">{t("hero.sub")}</p>

      {/* Live trust booster: "ATTIVO ORA · ~4 min · 3 tecnici in zona" */}
      <LiveResponseBadge lang={lang} className="mt-5 max-w-md" />

      <HeroCTAButtons callLabel="CHIAMA H24 — 351 062 4386" />

      {/* Geolocation-based proximity badge — sits below CTAs to avoid
          stealing attention from the primary action. */}
      <div className="mt-4">
        <NearestTechBadge lang={lang} />
      </div>

      <HeroTrust items={t("hero.trust") || []} />
    </div>
  );
}

function HeroImage() {
  // LCP image: REAL photo of Cristian (Bagnoman) at work with a Sewerin
  // correlator + geophone — pure social-proof, far more trustworthy than
  // any stock or AI image. Served as WebP with JPG fallback, mobile-first.
  return (
    <div className="relative">
      <div className="relative rounded-2xl overflow-hidden border border-[hsl(var(--border))] shadow-md">
        <picture>
          <source type="image/webp" media="(max-width: 480px)" srcSet="/images/real/hero-real-520.webp" />
          <source type="image/webp" media="(max-width: 640px)" srcSet="/images/real/hero-real-800.webp" />
          <source type="image/webp" srcSet="/images/real/hero-real.webp" />
          <source type="image/jpeg" media="(max-width: 480px)" srcSet="/images/real/hero-real-520.jpg" />
          <source type="image/jpeg" media="(max-width: 640px)" srcSet="/images/real/hero-real-800.jpg" />
          <img
            src="/images/real/hero-real.jpg"
            alt="Cristian La Rocca (Bagnoman) durante una ricerca perdite reale con correlatore acustico e geofono professionale"
            className="w-full h-[320px] sm:h-[460px] object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1200}
            height={900}
          />
        </picture>
        <div className="absolute bottom-3 left-3 right-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-xs font-semibold font-mono-tech text-[hsl(var(--brand-navy))]">Correlatore acustico</span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-xs font-semibold font-mono-tech text-[hsl(var(--brand-navy))]">Geofono professionale</span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--brand-urgent))]/95 backdrop-blur px-3 py-1.5 text-xs font-semibold text-white">FOTO REALE</span>
        </div>
      </div>
    </div>
  );
}

export function HeroSection() {
  const { t, lang } = useLang();
  return (
    <section className="relative overflow-hidden hero-gradient">
      <div className="max-w-[1120px] mx-auto px-4 sm:px-6 pt-10 pb-8 sm:pt-14 sm:pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 items-center">
          <HeroContent t={t} lang={lang} />
          <HeroImage />
        </div>
      </div>
    </section>
  );
}

export default HeroSection;
